import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useVehicles } from '../../../hooks/useVehicles';
import VehicleCard from '../../inventory/components/VehicleCard';
import NearbyAreasSection from '../components/NearbyAreasSection';
import ReadyToShopCta from '../components/ReadyToShopCta';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';

export default function IlwacoPage() {
  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rockhoundauto.com/' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://rockhoundauto.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Ilwaco', item: 'https://rockhoundauto.com/service-areas/ilwaco' },
      ],
    };
    injectJsonLd('schema-ilwaco-service-breadcrumb', breadcrumbSchema);
    return () => { removeJsonLd('schema-ilwaco-service-breadcrumb'); };
  }, []);

  const { vehicles, loading } = useVehicles();
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredVehicles = vehicles
    .filter(v => v.status === 'available')
    .filter(v => selectedType === 'all' || v.type === selectedType)
    .slice(0, 6);

  const nearbyAreas = [
    { name: 'Long Beach', slug: 'long-beach-wa' },
    { name: 'Astoria', slug: 'astoria' },
    { name: 'Warrenton', slug: 'warrenton' },
    { name: 'Seaside', slug: 'seaside' },
  ];

  return (
    <>
      <title>Used Cars Ilwaco WA | Free Delivery | Rockhound Auto</title>
      <meta name="description" content="Quality used cars delivered free to Ilwaco, Washington. No sales tax at purchase. Serving Long Beach Peninsula with transparent pricing and honest service." />
      <link rel="canonical" href="https://rockhoundauto.com/service-areas/ilwaco" />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/0ca7311f28a192758eb3d8fb0c360d5e.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            {/* CONTRAST FIX: text-teal-400 → text-teal-300 for WCAG AA on dark bg */}
            <div className="flex items-center gap-2 text-teal-300 mb-4">
              <i className="ri-map-pin-line text-xl"></i>
              <span className="font-semibold">Serving Ilwaco, Washington</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Used Cars in Ilwaco, Washington
            </h1>
            {/* CONTRAST FIX: text-slate-300 → text-slate-200 */}
            <p className="text-2xl text-slate-200 mb-8">
              Buy a Car. Skip the Dealership Circus.
            </p>
            {/* CONTRAST FIX: text-slate-400 → text-slate-300 */}
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Free delivery to Ilwaco, Cape Disappointment, and the Long Beach Peninsula. No pressure sales, no hidden fees, no wasted weekends. Just quality used cars and a buying process that respects your time.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/inventory" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap">
                View Inventory
              </Link>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap border border-white/20">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="bg-white py-8 border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">10 min</div>
              <div className="text-slate-600 text-sm">Drive from Ilwaco</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">Free</div>
              <div className="text-slate-600 text-sm">Delivery to Your Home</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">No Games</div>
              <div className="text-slate-600 text-sm">Straight Pricing</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">Fast</div>
              <div className="text-slate-600 text-sm">In and Out Process</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Ilwaco Residents Choose Rockhound */}
      <section className="py-16 bg-slate-50" aria-label="Why Ilwaco residents choose Rockhound Auto">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Ilwaco Residents Choose Rockhound Auto
            </h2>
            <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-6">
              <p>
                Ilwaco is a working fishing town with real character. Commercial fishing, charter boats, and people who value hard work, straight talk, and fair deals. You don&apos;t have time for dealership games, and you shouldn&apos;t have to play them.
              </p>
              <p><strong>Rockhound Auto brings a different approach to Ilwaco.</strong></p>
              <p>
                We&apos;re located in Warrenton, Oregon—just 10 minutes away across the Astoria-Megler Bridge. Close enough to visit easily, but we&apos;ll also deliver for free to your Ilwaco home. For a deeper look at everything we offer peninsula buyers, see our dedicated <Link to="/used-cars-ilwaco-washington" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">used cars Ilwaco Washington</Link> page.
              </p>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">What Makes Us Different for Ilwaco Buyers</h3>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-hand-coin-line text-2xl text-teal-600"></i>
                  Straight Pricing, No Negotiation Games
                </h4>
                <p className="text-slate-700 mb-0">
                  The price you see online is the real price. Not a "starting point." Not a bait-and-switch. We don&apos;t play the four-square game, we don&apos;t have a "manager" who needs to approve anything, and we don&apos;t add surprise fees at the end.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                  We Respect Your Time
                </h4>
                <p className="text-slate-700 mb-0">
                  Browse our inventory online. Ask questions via text or call. Schedule a test drive in Ilwaco or at our lot. Make a decision without pressure. We can complete the entire process in under an hour, or take days if you prefer.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-truck-line text-2xl text-teal-600"></i>
                  Free Delivery to Ilwaco
                </h4>
                <p className="text-slate-700 mb-0">
                  Found the right vehicle? We&apos;ll bring it to you. Free delivery anywhere in Ilwaco. Test drive it at your house, on your roads, near the port. If you love it, we&apos;ll complete the paperwork right there.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What Ilwaco Buyers Are Looking For</h3>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                {[
                  { icon: 'ri-truck-line', title: 'Work Trucks', desc: 'Ilwaco is a working town. Trucks for fishing, hauling gear, towing boats. We stock reliable pickups built for real work—Tacomas, F-150s, Silverados.' },
                  { icon: 'ri-compass-3-line', title: 'AWD & 4WD', desc: 'Coastal weather, boat ramps, and winter storms mean AWD and 4WD are essential. Subarus, RAV4s, and trucks with four-wheel drive are popular.' },
                  { icon: 'ri-tools-line', title: 'Reliability Over Flash', desc: 'Ilwaco buyers want vehicles that work, year after year. Toyota, Honda, Subaru, Ford—brands with proven track records and affordable parts.' },
                  { icon: 'ri-anchor-line', title: 'Towing Capacity', desc: 'For towing boats, trailers, and fishing equipment. We highlight towing specs and ensure vehicles can handle real work.' },
                ].map((item) => (
                  <div key={item.title} className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                    <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <i className={`${item.icon} text-xl text-teal-600`}></i>
                      {item.title}
                    </h4>
                    <p className="text-slate-700 text-sm mb-0">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-6">
                <h4 className="font-bold text-red-900 mb-3">The Traditional Dealership Experience:</h4>
                <ul className="space-y-2 text-slate-700">
                  {['Drive 60+ minutes to Longview, Kelso, or beyond','Spend 3-5 hours at the dealership (minimum)','Deal with high-pressure sales tactics and "manager approvals"','Negotiate pricing, then get hit with surprise fees in finance','Leave exhausted, wondering if you got a fair deal'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 my-6">
                <h4 className="font-bold text-teal-900 mb-3">The Rockhound Experience:</h4>
                <ul className="space-y-2 text-slate-700">
                  {['Browse inventory from your Ilwaco home','Text or call with questions—get straight answers','Free delivery to your Ilwaco location for test drives','Transparent pricing with no hidden fees or surprises','Complete the deal in under an hour, or take your time—your choice'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1 font-bold"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Popular Vehicles for Ilwaco Residents</h3>
              <div className="space-y-6 my-8">
                {[
                  { name: 'Toyota Tacoma', desc: 'The workhorse of fishing towns. Reliable, holds value incredibly well, available 4WD. Perfect for towing boats, hauling gear, and everything in between.' },
                  { name: 'Ford F-150', desc: "America's best-selling truck. Powerful, spacious, available in multiple configurations. Great for heavy-duty work, towing boats, and commercial fishing operations." },
                  { name: 'Chevrolet Silverado', desc: 'Tough, capable, and built for work. Available in multiple cab and bed configurations. Great for fishing and hauling heavy loads.' },
                  { name: 'Subaru Outback', desc: 'For families or anyone who needs AWD without a truck. Reliable, great in rain and snow, excellent cargo space. Perfect for daily driving and coastal weather.' },
                  { name: 'Toyota Tundra', desc: 'For serious towing and heavy-duty work. Full-size truck with legendary Toyota reliability. Perfect for commercial fishing operations and large boat trailers.' },
                ].map((v) => (
                  <div key={v.name} className="border-l-4 border-teal-500 pl-6">
                    <h4 className="font-bold text-slate-900 mb-2">{v.name}</h4>
                    <p className="text-slate-700 mb-0">{v.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-8 my-8">
                <h4 className="text-2xl font-bold mb-4">Get Pre-Qualified in Minutes</h4>
                <p className="text-slate-300 mb-6">See your financing options before you even visit. No impact on your credit score, no obligation.</p>
                <Link to="/apply-for-financing" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Apply for Financing
                </Link>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">Cross-State Car Buying Made Easy</h3>
              <p>Buying a car in Oregon when you live in Washington is easier than you think. We handle all the paperwork, including:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Washington title and registration</li>
                <li>Washington sales tax collection</li>
                <li>Temporary permits for driving home</li>
                <li>All DMV paperwork and submissions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-white" aria-label="Available vehicles near Ilwaco">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Available Vehicles Near Ilwaco</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Browse our current inventory. All vehicles are inspected, detailed, and ready for delivery to Ilwaco.
            </p>
          </div>

          {/* TOUCH TARGET FIX: py-2 → py-3, added min-h-[44px] */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" role="group" aria-label="Filter vehicles by type">
            {[
              { label: 'All Vehicles', value: 'all' },
              { label: 'Trucks', value: 'Truck' },
              { label: 'SUVs', value: 'SUV' },
              { label: 'Sedans', value: 'Sedan' },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setSelectedType(value)}
                aria-pressed={selectedType === value}
                className={`px-6 py-3 rounded-full font-semibold transition-colors whitespace-nowrap min-h-[44px] cursor-pointer ${
                  selectedType === value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link to="/inventory" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap">
                  View All Inventory
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <i className="ri-car-line text-6xl text-slate-300 mb-4"></i>
              <p className="text-xl text-slate-600 mb-6">No vehicles currently match your filter. Check back soon or view all inventory.</p>
              <Link to="/inventory" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Service Area Map */}
      <section className="py-16 bg-slate-50" aria-label="Ilwaco service area map">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center text-slate-900">
              Free Delivery to Ilwaco &amp; Surrounding Areas
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11174.5!2d-124.0329!3d46.3082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54940a0000000001%3A0xabcdef0000000002!2sIlwaco%2C%20WA%2098624!5e0!3m2!1sen!2sus!4v1700000000004!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ilwaco Washington Service Area Map"
                ></iframe>
              </div>
              <div className="p-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { icon: 'ri-map-pin-line', title: 'Our Location', desc: 'Warrenton, OR\n10 min from Ilwaco' },
                    { icon: 'ri-truck-line', title: 'Free Delivery', desc: 'To your Ilwaco\nlocation' },
                    { icon: 'ri-time-line', title: 'Fast Process', desc: 'Complete in under\nan hour' },
                  ].map((item) => (
                    <div key={item.title} className="text-center">
                      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className={`${item.icon} text-3xl text-teal-600`}></i>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600 text-sm whitespace-pre-line">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ilwaco Buyer Guide Callout */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Full Ilwaco Buyer Guide</h4>
                <p className="text-slate-600 text-sm">
                  See our dedicated Ilwaco page — no-sales-tax buying from Oregon, cross-state paperwork explained, and why Long Beach Peninsula fishing families choose Rockhound Auto.
                </p>
              </div>
              <Link
                to="/used-cars-ilwaco-washington"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm min-h-[44px]"
              >
                Used Cars Ilwaco WA <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Ilwaco" nearbyAreas={nearbyAreas} />
      <ReadyToShopCta cityName="Ilwaco" />
    </>
  );
}
